import { useCallback, useEffect, useRef, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Table,
  Alert,
  Spinner,
} from "react-bootstrap";
import moment from "moment";

import MediosDePago from "./MediosDePago";

import {
  postOrdenCobroRemito,
  getPendientesOrdenCobroRemito,
  getCreditosDisponiblesOrdenCobroRemito,
} from "../helpers/rutaOrdenesCobroRemito";

const dinero = (valor) =>
  Number(valor || 0).toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
  });

const centavos = (valor) => {
  const n = Number(valor === "" ? 0 : valor);

  if (!Number.isFinite(n) || n < 0) return null;

  const c = Math.round(n * 100);

  if (
    !Number.isSafeInteger(c) ||
    Math.abs(n * 100 - c) > 0.000001
  ) {
    return null;
  }

  return c;
};

function fechaAPI(valor) {
  if (!valor) return undefined;

  const s = String(valor);

  return /^\d{4}-\d{2}-\d{2}$/.test(s)
    ? `${s}T00:00:00-03:00`
    : s;
}

function serializarMedio(m, fechaOrden) {
  const tipos = {
    EFECTIVO: "efectivo",
    TRANSFERENCIA: "transferencia",
    CHEQUE: "cheque_tercero",
    CHEQUE_TERCERO: "cheque_tercero",
    ICHEQ: "echeq",
    ECHEQ: "echeq",
  };

  const tipo = tipos[String(m.tipo || "").trim().toUpperCase()];

  if (!tipo) {
    throw new Error("Hay un medio de cobro sin tipo válido.");
  }

  const importe = centavos(m.importe);

  if (importe === null || importe <= 0) {
    throw new Error("Los medios deben tener importes válidos mayores que cero.");
  }

  const datos = {
    tipo,
    importe: importe / 100,
    fecha: fechaAPI(m.fecha || fechaOrden),
    fechaValor: fechaAPI(m.fechaValor),
  };

  if (tipo === "efectivo") {
    if (!m.caja) throw new Error("Seleccione la caja.");
    datos.caja = m.caja;
  }

  if (tipo === "transferencia") {
    if (!m.cuentaBancaria) {
      throw new Error("Seleccione la cuenta bancaria.");
    }

    datos.cuentaBancaria = m.cuentaBancaria;
    datos.refTransferencia =
      m.refTransferencia || m.referencia || m.numero || "";
  }

  if (tipo === "cheque_tercero") {
    if (!m.nroCheque) throw new Error("Ingrese el número del cheque.");

    Object.assign(datos, {
      nroCheque: m.nroCheque,
      bancoEmisor: m.bancoEmisor || "",
      librador: m.librador || "",
      cuitLibrador: m.cuitLibrador || "",
      fechaPagoDiferido: fechaAPI(m.fechaPagoDiferido),
    });
  }

  if (tipo === "echeq") {
    if (!m.idICheq) throw new Error("Ingrese el ID del eCheq.");

    Object.assign(datos, {
      idICheq: m.idICheq,
      bancoEmisor: m.bancoEmisor || "",
      emisor: m.emisor || "",
      fechaLiquidacion: fechaAPI(m.fechaLiquidacion),
    });
  }

  // No enviamos el _id temporal que usa MediosDePago en pantalla.
  return datos;
}

export default function ModalOrdenCobroRemito({
  show,
  onHide,
  cliente,
}) {
  const clienteId =
    typeof cliente === "string" ? cliente : cliente?._id;

  const [pendientes, setPendientes] = useState([]);
  const [creditos, setCreditos] = useState([]);
  const [medios, setMedios] = useState([]);
  const [retenciones, setRetenciones] = useState([]);

  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [observaciones, setObservaciones] = useState("");

  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [cargaCompleta, setCargaCompleta] = useState(false);
  const [mediosValidos, setMediosValidos] = useState(false);
  const [error, setError] = useState("");

  const enviando = useRef(false);

  const onValidityChange = useCallback(({ valid }) => {
    setMediosValidos(valid);
  }, []);

  useEffect(() => {
    if (!show) return undefined;

    let cancelado = false;

    setPendientes([]);
    setCreditos([]);
    setMedios([]);
    setRetenciones([]);
    setFecha(moment().format("YYYY-MM-DD"));
    setObservaciones("");
    setMediosValidos(false);
    setError("");
    setCargaCompleta(false);

    if (!clienteId) {
      setCargando(false);
      setError("Seleccione un cliente para registrar el cobro.");
      return undefined;
    }

    setCargando(true);

    async function cargar() {
      try {
        const [resPendientes, resCreditos] = await Promise.all([
          getPendientesOrdenCobroRemito(clienteId),
          getCreditosDisponiblesOrdenCobroRemito(clienteId),
        ]);

        if (cancelado) return;

        if (!resPendientes?.ok) {
          throw new Error(
            resPendientes?.msg || "No se pudieron cargar los remitos."
          );
        }

        if (!resCreditos?.ok) {
          throw new Error(
            resCreditos?.msg || "No se pudieron cargar los anticipos."
          );
        }

        setPendientes(
          (resPendientes.pendientes || []).map((r) => ({
            ...r,
            seleccionado: false,
            aplicadoA: "",
            aplicadoB: "",
          }))
        );

        setCreditos(
          (resCreditos.creditos || []).map((c) => ({
            ...c,
            importe: "",
          }))
        );

        setCargaCompleta(true);
      } catch (e) {
        if (!cancelado) setError(e.message);
      } finally {
        if (!cancelado) setCargando(false);
      }
    }

    cargar();

    return () => {
      cancelado = true;
    };
  }, [show, clienteId]);

  function cambiarRemito(id, patch) {
    setPendientes((actuales) =>
      actuales.map((r) => (r._id === id ? { ...r, ...patch } : r))
    );
  }

  function seleccionarRemito(r, seleccionado) {
    cambiarRemito(r._id, {
      seleccionado,
      aplicadoA: seleccionado ? Math.max(0, Number(r.saldoA || 0)) : "",
      aplicadoB: seleccionado ? Math.max(0, Number(r.saldoB || 0)) : "",
    });
  }

  function cambiarCredito(id, importe) {
    setCreditos((actuales) =>
      actuales.map((c) => (c._id === id ? { ...c, importe } : c))
    );
  }

  function cambiarRetencion(indice, patch) {
    setRetenciones((actuales) =>
      actuales.map((r, i) => (i === indice ? { ...r, ...patch } : r))
    );
  }

  // Totales en centavos para evitar diferencias por decimales.
  const seleccionados = pendientes.filter((r) => r.seleccionado);

  const aplicadoA = seleccionados.reduce(
    (total, r) => total + (centavos(r.aplicadoA) ?? 0),
    0
  );

  const aplicadoB = seleccionados.reduce(
    (total, r) => total + (centavos(r.aplicadoB) ?? 0),
    0
  );

  const totalAplicado = aplicadoA + aplicadoB;

  const totalMedios = medios.reduce(
    (total, m) => total + (centavos(m.importe) ?? 0),
    0
  );

  const totalRetenciones = retenciones.reduce(
    (total, r) => total + (centavos(r.importe) ?? 0),
    0
  );

  const totalCreditos = creditos.reduce(
    (total, c) => total + (centavos(c.importe) ?? 0),
    0
  );

  const disponible = totalMedios + totalRetenciones + totalCreditos;
  const diferencia = disponible - totalAplicado;

  let validacion = "";

  for (const r of seleccionados) {
    const a = centavos(r.aplicadoA);
    const b = centavos(r.aplicadoB);

    if (a === null || b === null) {
      validacion = "Los importes A/B deben ser positivos o cero y tener hasta dos decimales.";
      break;
    }

    if (
      a > (centavos(r.saldoA) ?? 0) ||
      b > (centavos(r.saldoB) ?? 0)
    ) {
      validacion = `La aplicación supera el saldo del remito ${r.codrem}.`;
      break;
    }

    if (a + b === 0) {
      validacion = `Ingrese un importe o desmarque el remito ${r.codrem}.`;
      break;
    }
  }

  for (const c of creditos) {
    const importe = centavos(c.importe);

    if (
      importe === null ||
      importe > (centavos(c.disponible) ?? 0)
    ) {
      validacion = `Importe de anticipo inválido en ${c.numero}.`;
      break;
    }
  }

  for (const r of retenciones) {
    const importe = centavos(r.importe);

    if (importe === null || importe <= 0) {
      validacion = "Complete el importe de cada retención o quite su fila.";
      break;
    }
  }

  if (medios.some((m) => {
    const importe = centavos(m.importe);
    return importe === null || importe <= 0;
  })) {
    validacion = "Revise los importes de los medios de cobro.";
  }

  if (!validacion && totalCreditos > totalAplicado) {
    validacion = "Los anticipos utilizados no pueden superar lo aplicado a remitos.";
  }

  if (!validacion && diferencia < 0) {
    validacion = "Los medios, retenciones y anticipos no cubren lo aplicado.";
  }

  const puedeGuardar =
    Boolean(clienteId) &&
    Boolean(fecha) &&
    cargaCompleta &&
    !cargando &&
    !guardando &&
    !validacion &&
    disponible > 0 &&
    (medios.length === 0 || mediosValidos);

  async function guardar() {
    if (!puedeGuardar || enviando.current) return;

    enviando.current = true;
    setGuardando(true);
    setError("");

    try {
      const payload = {
        cliente: clienteId,
        fecha,
        observaciones,

        aplicaciones: seleccionados.map((r) => ({
          remito: r._id,
          aplicadoA: centavos(r.aplicadoA) / 100,
          aplicadoB: centavos(r.aplicadoB) / 100,
        })),

        saldosFavorAplicados: creditos
          .filter((c) => (centavos(c.importe) || 0) > 0)
          .map((c) => ({
            ordenCobroOrigen: c._id,
            importe: centavos(c.importe) / 100,
          })),

        medios: medios.map((m) => serializarMedio(m, fecha)),

        retenciones: retenciones.map((r) => ({
          tipo: r.tipo,
          numero: r.numero || "",
          fecha: fechaAPI(r.fecha || fecha),
          importe: centavos(r.importe) / 100,
        })),
      };

      const respuesta = await postOrdenCobroRemito(payload);

      if (!respuesta?.ok) {
        throw new Error(
          respuesta?.msg || "No se pudo guardar la orden de cobro."
        );
      }

      // Evita volver a guardar este formulario si el padre no lo cierra.
      setCargaCompleta(false);
      onHide?.(true, respuesta.orden);
    } catch (e) {
      setError(e.message);
    } finally {
      enviando.current = false;
      setGuardando(false);
    }
  }

  function cerrar() {
    if (!enviando.current) onHide?.(false);
  }

  return (
    <Modal
      show={show}
      onHide={cerrar}
      size="xl"
      backdrop="static"
      keyboard={!guardando}
    >
      <Modal.Header closeButton={!guardando}>
        <Modal.Title>Nueva orden de cobro · Papas</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {cargando && (
          <div className="mb-3">
            <Spinner animation="border" size="sm" /> Cargando saldos…
          </div>
        )}

        <fieldset disabled={cargando || guardando || !cargaCompleta}>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Label>Cliente</Form.Label>
              <Form.Control
                value={cliente?.razonsocial || clienteId || ""}
                readOnly
              />
            </Col>

            <Col md={3}>
              <Form.Label>Fecha</Form.Label>
              <Form.Control
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
              />
            </Col>

            <Col md={3}>
              <Form.Label>Número de OC</Form.Label>
              <Form.Control value="Automático al guardar" readOnly />
            </Col>
          </Row>

          <div
            className="p-3 mb-3 rounded"
            style={{ background: "#edf4ff", color: "#17488b" }}
          >
            <Row>
              <Col md={3}>Aplicado A: <strong>{dinero(aplicadoA / 100)}</strong></Col>
              <Col md={3}>Aplicado B: <strong>{dinero(aplicadoB / 100)}</strong></Col>
              <Col md={3}>Total aplicado: <strong>{dinero(totalAplicado / 100)}</strong></Col>
              <Col md={3}>Disponible: <strong>{dinero(disponible / 100)}</strong></Col>
            </Row>
          </div>

          <div className="d-flex flex-wrap gap-2 mb-2">
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() =>
                setPendientes((actuales) =>
                  actuales.map((r) => ({
                    ...r,
                    seleccionado: true,
                    aplicadoA: Math.max(0, Number(r.saldoA || 0)),
                    aplicadoB: Math.max(0, Number(r.saldoB || 0)),
                  }))
                )
              }
            >
              Seleccionar todos
            </Button>

            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() =>
                setPendientes((actuales) =>
                  actuales.map((r) => ({
                    ...r,
                    seleccionado: false,
                    aplicadoA: "",
                    aplicadoB: "",
                  }))
                )
              }
            >
              Limpiar selección
            </Button>
          </div>

          <div style={{ maxHeight: 320, overflow: "auto" }}>
            <Table striped bordered hover size="sm">
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  background: "#edf4ff",
                  zIndex: 1,
                }}
              >
                <tr>
                  <th style={{ width: 48, minWidth: 48, textAlign: "center" }}> Sel.</th>
                  <th>Remito</th>
                  <th>Fecha</th>
                  <th>Saldo A</th>
                  <th>Saldo B</th>
                  <th>Aplicar A</th>
                  <th>Aplicar B</th>
                </tr>
              </thead>

              <tbody>
                {pendientes.map((r) => (
                  <tr key={r._id}>
                    <td
                    style={{
                        width: 48,
                        minWidth: 48,
                        textAlign: "center",
                        verticalAlign: "middle",
                    }}
                    >
                    <input
                        type="checkbox"
                        aria-label={`Seleccionar remito ${r.codrem}`}
                        checked={Boolean(r.seleccionado)}
                        onChange={(e) =>
                        seleccionarRemito(r, e.target.checked)
                        }
                        style={{
                        position: "static",
                        float: "none",
                        display: "inline-block",
                        margin: 0,
                        width: 16,
                        height: 16,
                        verticalAlign: "middle",
                        cursor: "pointer",
                        accentColor: "#0d6efd",
                        }}
                    />
                    </td>
                    <td>{r.codrem}</td>
                    <td>{moment(r.fecha).format("DD/MM/YYYY")}</td>
                    <td className="text-end">{dinero(r.saldoA)}</td>
                    <td className="text-end">{dinero(r.saldoB)}</td>

                    {["A", "B"].map((parte) => (
                      <td key={parte} style={{ minWidth: 135 }}>
                        <Form.Control
                          size="sm"
                          type="number"
                          min="0"
                          step="0.01"
                          max={Math.max(0, Number(r[`saldo${parte}`] || 0))}
                          disabled={!r.seleccionado}
                          value={r[`aplicado${parte}`]}
                          aria-label={`Aplicar ${parte} remito ${r.codrem}`}
                          onChange={(e) =>
                            cambiarRemito(r._id, {
                              [`aplicado${parte}`]: e.target.value,
                            })
                          }
                        />
                      </td>
                    ))}
                  </tr>
                ))}

                {!cargando && pendientes.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center">
                      No hay remitos pendientes. Puede registrar un anticipo.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {creditos.length > 0 && (
            <>
              <h6 className="mt-3">Anticipos disponibles del cliente</h6>

              <Table bordered size="sm" responsive>
                <thead>
                  <tr>
                    <th>OC de origen</th>
                    <th>Disponible</th>
                    <th>Importe a utilizar</th>
                  </tr>
                </thead>
                <tbody>
                  {creditos.map((c) => (
                    <tr key={c._id}>
                      <td>{c.numero}</td>
                      <td>{dinero(c.disponible)}</td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="number"
                          min="0"
                          step="0.01"
                          max={c.disponible}
                          value={c.importe}
                          onChange={(e) =>
                            cambiarCredito(c._id, e.target.value)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}

          <div style={{ overflowX: "auto" }} className="mt-3">
            {show && clienteId && cargaCompleta && (
              <MediosDePago
                key={clienteId}
                medios={medios}
                setMedios={setMedios}
                onValidityChange={onValidityChange}
              />
            )}
          </div>

          <div className="d-flex align-items-center gap-2 mt-3 mb-2">
            <h6 className="mb-0">Retenciones</h6>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() =>
                setRetenciones((actuales) => [
                  ...actuales,
                  {
                    tipo: "iibb",
                    numero: "",
                    fecha,
                    importe: "",
                  },
                ])
              }
            >
              Agregar retención
            </Button>
          </div>

          {retenciones.map((r, i) => (
            <Row className="mb-2" key={i}>
              <Col md={3}>
                <Form.Select
                  value={r.tipo}
                  onChange={(e) =>
                    cambiarRetencion(i, { tipo: e.target.value })
                  }
                >
                  <option value="iibb">IIBB</option>
                  <option value="ganancias">Ganancias</option>
                  <option value="suss">SUSS</option>
                  <option value="iva">IVA</option>
                  <option value="otra">Otra</option>
                </Form.Select>
              </Col>

              <Col md={3}>
                <Form.Control
                  placeholder="N.º comprobante"
                  value={r.numero}
                  onChange={(e) =>
                    cambiarRetencion(i, { numero: e.target.value })
                  }
                />
              </Col>

              <Col md={2}>
                <Form.Control
                  type="date"
                  value={r.fecha}
                  onChange={(e) =>
                    cambiarRetencion(i, { fecha: e.target.value })
                  }
                />
              </Col>

              <Col md={3}>
                <Form.Control
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="Importe"
                  value={r.importe}
                  onChange={(e) =>
                    cambiarRetencion(i, { importe: e.target.value })
                  }
                />
              </Col>

              <Col md={1}>
                <Button
                  variant="outline-danger"
                  onClick={() =>
                    setRetenciones((actuales) =>
                      actuales.filter((_, indice) => indice !== i)
                    )
                  }
                >
                  ×
                </Button>
              </Col>
            </Row>
          ))}

          <Form.Group className="mt-3">
            <Form.Label>Observaciones</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
            />
          </Form.Group>
        </fieldset>

        <div className="mt-3 p-3 border rounded">
          <div>Medios recibidos: <strong>{dinero(totalMedios / 100)}</strong></div>
          <div>Retenciones: <strong>{dinero(totalRetenciones / 100)}</strong></div>
          <div>Anticipos utilizados: <strong>{dinero(totalCreditos / 100)}</strong></div>
          <div>Aplicado a remitos: <strong>{dinero(totalAplicado / 100)}</strong></div>
          <div>
            Saldo a favor generado:{" "}
            <strong>{dinero(Math.max(0, diferencia) / 100)}</strong>
          </div>
        </div>

        {validacion && (
          <Alert variant="warning" className="mt-3 mb-0">
            {validacion}
          </Alert>
        )}

        {medios.length > 0 && !mediosValidos && (
          <div className="text-danger mt-2">
            Complete los datos obligatorios de los medios de cobro.
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          disabled={guardando}
          onClick={cerrar}
        >
          Cancelar
        </Button>

        <Button
          variant="primary"
          disabled={!puedeGuardar}
          onClick={guardar}
        >
          {guardando ? "Guardando…" : "Guardar orden de cobro"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}